import styled, { keyframes, css } from 'styled-components';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  70% { transform: scale(1.7); opacity: 0; }
  100% { transform: scale(1.7); opacity: 0; }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

// Theme colors matching portfolio
export const getThemeColors = (isDarkMode: boolean) => ({
  primary: '#f34452',
  primaryHover: '#d63946',
  background: isDarkMode ? '#2d2d2d' : '#ffffff',
  surface: isDarkMode ? '#3a3a3a' : '#f8f8f8',
  surfaceHover: isDarkMode ? '#4a4a4a' : '#e0e0e0',
  textPrimary: isDarkMode ? '#ffffff' : '#2d2d2d',
  textSecondary: isDarkMode ? '#999999' : '#666666',
  borderColor: isDarkMode ? '#4a4a4a' : '#d0d0d0',
  userMessage: '#f34452',
  assistantMessage: isDarkMode ? '#3a3a3a' : '#f5f5f5',
  shadow: isDarkMode
    ? '0 4px 6px -1px rgba(0, 0, 0, 0.5)'
    : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  shadowLg: isDarkMode
    ? '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)'
    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
  focusRing: 'rgba(243, 68, 82, 0.3)',
});

// ============================
// UNIFIED FAB (mic + chat pill)
// ============================

export const UnifiedFAB = styled.div<{ isHidden: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  height: 60px;
  background: linear-gradient(135deg, #f34452 0%, #d63946 100%);
  border-radius: 30px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2);
  opacity: ${p => (p.isHidden ? 0 : 1)};
  transform: ${p => (p.isHidden ? 'scale(0.85) translateY(6px)' : 'scale(1) translateY(0)')};
  pointer-events: ${p => (p.isHidden ? 'none' : 'all')};
  transition: opacity 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 30px;
    background: #f34452;
    animation: ${pulse} 2s ease-out infinite;
    z-index: -1;
  }

  &:hover {
    box-shadow: 0 25px 30px -5px rgba(0, 0, 0, 0.4), 0 15px 15px -5px rgba(0, 0, 0, 0.3);
    transform: ${p => (p.isHidden ? 'scale(0.85) translateY(6px)' : 'scale(1.04) translateY(0)')};
  }

  @media (max-width: 768px) {
    bottom: 16px;
    right: 16px;
    height: 56px;
  }
`;

export const FabBtn = styled.button<{ isActive?: boolean }>`
  width: 56px;
  height: 60px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: background 0.15s;
  flex-shrink: 0;
  border-radius: 30px;

  &:hover {
    background: rgba(0, 0, 0, 0.12);
  }

  &:focus {
    outline: none;
    background: rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: scale(0.92);
  }

  /* pulsing mic icon when call is active */
  ${p => p.isActive && css`
    svg {
      animation: ${pulse} 1.5s ease-out infinite;
      opacity: 0.9;
    }
  `}

  @media (max-width: 768px) {
    width: 52px;
    height: 56px;
  }
`;

export const FabDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`;

export const FabBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 6px;
  background: white;
  color: #f34452;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  padding: 0 3px;
  pointer-events: none;
`;

// Legacy — kept so ChatButton.tsx still compiles but no longer rendered
export const ChatButtonWrapper = styled.button<{ isHidden: boolean; inSpeedDial?: boolean }>`
  display: none;
`;

export const NotificationBadge = styled.span<{ visible: boolean }>`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  border-radius: 10px;
  min-width: 20px;
  height: 20px;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  padding: 0 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

// ============================
// WIDGET CONTAINER
// ============================

export const WidgetWrapper = styled.div<{
  isExpanded: boolean;
  isMaximized: boolean;
  isDarkMode: boolean;
}>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      position: fixed;
      bottom: ${props.isMaximized ? '0' : '20px'};
      right: ${props.isMaximized ? '0' : '20px'};
      width: ${props.isMaximized ? '100vw' : '420px'};
      height: ${props.isMaximized ? '100vh' : '650px'};
      border-radius: ${props.isMaximized ? '0' : '16px'};
      background: ${colors.background};
      border-radius: 16px;
      box-shadow: ${colors.shadowLg};
      z-index: 100000;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid ${colors.borderColor};
      opacity: ${props.isExpanded ? 1 : 0};
      transform: ${props.isExpanded ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
      pointer-events: ${props.isExpanded ? 'all' : 'none'};
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      @media (max-width: 768px) {
        width: 100vw;
        height: 100vh;
        bottom: 0;
        right: 0;
        border-radius: 0;
      }
    `;
  }}
`;

// ============================
// WIDGET HEADER
// ============================

export const Header = styled.div<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      background: ${colors.surface};
      border-bottom: 1px solid ${colors.borderColor};
      padding: 1rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
    `;
  }}
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`;

export const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #f34452;
`;

export const TitleText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled.h2<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      font-size: 1rem;
      font-weight: 600;
      color: ${colors.textPrimary};
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `;
  }}
`;

export const Subtitle = styled.p<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      font-size: 0.75rem;
      color: ${colors.textSecondary};
      margin: 0;
    `;
  }}
`;

export const Controls = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

export const ControlButton = styled.button<{
  isDarkMode: boolean;
  isClose?: boolean;
  isActive?: boolean;
}>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: transparent;
      border: 1px solid ${props.isActive ? colors.primary : colors.borderColor};
      color: ${props.isActive ? colors.primary : colors.textSecondary};
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      position: relative;

      &:hover {
        background: ${props.isClose ? '#ef4444' : colors.surfaceHover};
        color: ${props.isClose ? 'white' : colors.textPrimary};
        border-color: ${props.isClose ? '#ef4444' : colors.primary};
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px ${colors.focusRing};
      }

      &:active {
        transform: scale(0.95);
      }
    `;
  }}
`;

// ============================
// MAIN CONTENT
// ============================

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

// ============================
// SIDEBAR
// ============================

export const Sidebar = styled.aside<{
  isOpen: boolean;
  isDarkMode: boolean;
}>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      width: ${props.isOpen ? '170px' : '0'};
      background: ${colors.surface};
      border-right: 1px solid ${colors.borderColor};
      overflow-y: auto;
      overflow-x: hidden;
      flex-shrink: 0;
      transition: width 0.3s ease, padding 0.3s ease;
      padding: ${props.isOpen ? '0.5rem' : '0'};

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: ${colors.surface};
      }

      &::-webkit-scrollbar-thumb {
        background: ${colors.borderColor};
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: ${colors.textSecondary};
      }

      @media (max-width: 768px) {
        position: relative;
        width: 100%;
        max-height: ${props.isOpen ? '200px' : '0'};
        overflow-x: hidden;
        overflow-y: ${props.isOpen ? 'auto' : 'hidden'};
        border-right: none;
        border-bottom: ${props.isOpen ? `1px solid ${colors.borderColor}` : 'none'};
        padding: ${props.isOpen ? '0.5rem' : '0'};
        transition: max-height 0.3s ease, padding 0.3s ease;
        z-index: 1;
        box-shadow: none;
      }
    `;
  }}
`;

export const SidebarSection = styled.div<{ isOpen: boolean }>`
  margin-bottom: 1rem;
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: opacity 0.3s ease 0.1s;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SidebarHeader = styled.summary<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      font-size: 0.75rem;
      font-weight: 600;
      color: ${colors.textSecondary};
      cursor: pointer;
      padding: 0.5rem 0;
      list-style: none;
      outline: none;
      transition: color 0.2s ease;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      user-select: none;

      &:hover {
        color: ${colors.primary};
      }

      &::-webkit-details-marker {
        display: none;
      }

      &::before {
        content: '▶';
        display: inline-block;
        margin-right: 0.5rem;
        transition: transform 0.2s ease;
        font-size: 0.65rem;
      }

      details[open] > &::before {
        transform: rotate(90deg);
      }
    `;
  }}
`;

export const SuggestedItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0;
`;

export const SuggestedItem = styled.button<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      padding: 0.5rem 0.75rem;
      background: ${colors.background};
      border: 1px solid ${colors.borderColor};
      border-radius: 6px;
      color: ${colors.textPrimary};
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.2s ease;
      text-align: left;
      width: 100%;

      &:hover {
        background: ${colors.surfaceHover};
        border-color: ${colors.primary};
        color: ${colors.primary};
        transform: translateX(2px);
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 2px ${colors.focusRing};
      }
    `;
  }}
`;

// ============================
// CHAT AREA
// ============================

export const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const MessagesContainer = styled.div<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      background: ${colors.background};

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-track {
        background: ${colors.background};
      }

      &::-webkit-scrollbar-thumb {
        background: ${colors.borderColor};
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: ${colors.textSecondary};
      }
    `;
  }}
`;

export const MessageWrapper = styled.div<{
  role: 'user' | 'assistant';
  isWelcome?: boolean;
}>`
  max-width: 85%;
  align-self: ${props => props.role === 'user' ? 'flex-end' : 'flex-start'};
  animation: ${fadeIn} 0.3s ease-out;
`;

export const MessageContent = styled.div<{
  role: 'user' | 'assistant';
  isDarkMode: boolean;
  isWelcome?: boolean;
}>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    const bgColor = props.role === 'user'
      ? colors.userMessage
      : colors.assistantMessage;
    const textColor = props.role === 'user'
      ? 'white'
      : colors.textPrimary;
    const borderRadius = props.role === 'user'
      ? '16px 16px 4px 16px'
      : '16px 16px 16px 4px';

    return css`
      padding: 0.625rem 1rem;
      border-radius: ${borderRadius};
      word-wrap: break-word;
      line-height: 1.5;
      font-size: 0.875rem;
      background: ${bgColor};
      color: ${textColor};
      ${props.isWelcome && `
        border: 2px solid ${colors.borderColor};
        box-shadow: ${colors.shadow};
      `}

      p {
        margin: 0.5rem 0;
        &:first-child { margin-top: 0; }
        &:last-child { margin-bottom: 0; }
      }

      ul, ol {
        margin: 0.5rem 0;
        padding-left: 1.5rem;
      }

      li {
        margin: 0.25rem 0;
      }

      code {
        background-color: rgba(0, 0, 0, 0.2);
        padding: 0.125rem 0.25rem;
        border-radius: 3px;
        font-family: 'Fira Code', 'Consolas', monospace;
        font-size: 0.8em;
      }

      pre {
        background-color: rgba(0, 0, 0, 0.2);
        padding: 0.5rem;
        border-radius: 4px;
        overflow-x: auto;
        margin: 0.5rem 0;

        code {
          background-color: transparent;
          padding: 0;
        }
      }

      h1, h2, h3, h4, h5, h6 {
        color: ${colors.textSecondary};
        font-weight: 600;
        margin: 0.5rem 0 0.25rem 0;
        line-height: 1.3;
      }

      h1 { font-size: 1rem; }
      h2 { font-size: 0.9rem; }
      h3 { font-size: 0.85rem; }
      h4, h5, h6 { font-size: 0.8rem; }

      strong {
        font-weight: 600;
      }

      em {
        font-style: italic;
      }
    `;
  }}
`;

export const SourcesCollapsible = styled.details<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      margin-top: 0.5rem;
      font-size: 0.7rem;
      color: ${colors.textSecondary};

      summary {
        cursor: pointer;
        padding: 0.25rem 0.5rem;
        user-select: none;
        font-weight: 500;

        &:hover {
          color: ${colors.textPrimary};
        }
      }

      &[open] summary {
        margin-bottom: 0.25rem;
      }
    `;
  }}
`;

export const SourcesContent = styled.div<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      padding: 0 0.5rem 0.25rem 1rem;
      color: ${colors.textSecondary};
    `;
  }}
`;

export const SourceLink = styled.a<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      color: ${colors.primary};
      text-decoration: none;
      padding: 0.125rem 0.375rem;
      border-radius: 4px;
      background: rgba(243, 68, 82, 0.1);
      border: 1px solid rgba(243, 68, 82, 0.2);
      display: inline-block;
      margin: 0.125rem 0.25rem 0.125rem 0;
      font-weight: 500;
      transition: all 0.2s ease;
      font-size: 0.65rem;

      &:hover {
        color: white;
        background: ${colors.primary};
        border-color: ${colors.primary};
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(243, 68, 82, 0.3);
      }

      &::after {
        content: '↗';
        font-size: 0.75em;
        margin-left: 0.25rem;
        opacity: 0.7;
      }

      &:hover::after {
        opacity: 1;
      }
    `;
  }}
`;

export const LoadingIndicator = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 0.5rem 1rem;

  span {
    width: 6px;
    height: 6px;
    background: #999;
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
`;

// ============================
// CHAT INPUT
// ============================

export const InputContainer = styled.div<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      display: flex;
      gap: 0.5rem;
      padding: 1rem;
      background: ${colors.background};
      border-top: 1px solid ${colors.borderColor};
      flex-shrink: 0;
    `;
  }}
`;

export const Input = styled.input<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      flex: 1;
      padding: 0.75rem 1rem;
      background: ${colors.surface};
      border: 1px solid ${colors.borderColor};
      border-radius: 20px;
      color: ${colors.textPrimary};
      font-size: 0.875rem;
      transition: all 0.2s ease;

      &:focus {
        outline: none;
        border-color: ${colors.primary};
        box-shadow: 0 0 0 3px ${colors.focusRing};
      }

      &::placeholder {
        color: ${colors.textSecondary};
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
  }}
`;

// ============================
// VOICE PLAYBACK
// ============================

export const VoiceContainer = styled.div`
  margin-top: 0.5rem;
`;

export const VoiceButton = styled.button<{
  isDarkMode: boolean;
  isError: boolean;
  voiceState: string;
}>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    const isPlaying = props.voiceState === 'playing';
    return css`
      background: transparent;
      border: 1px solid ${isPlaying ? colors.primary : colors.borderColor};
      border-radius: 12px;
      padding: 0 0.75rem;
      height: 44px;
      font-size: 0.75rem;
      color: ${props.isError ? colors.textSecondary : isPlaying ? colors.primary : colors.textSecondary};
      cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
      opacity: ${props.disabled ? 0.6 : 1};
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      transition: all 0.2s ease;
      white-space: nowrap;
      max-width: 100%;

      &:hover:not(:disabled) {
        background: ${colors.surfaceHover};
        border-color: ${colors.primary};
        color: ${colors.primary};
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px ${colors.focusRing};
      }

      &:active:not(:disabled) {
        transform: scale(0.97);
      }
    `;
  }}
`;

export const VoiceSpinner = styled.span`
  width: 10px;
  height: 10px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

export const VoiceDisclosure = styled.p<{ isDarkMode: boolean }>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      font-size: 0.65rem;
      color: ${colors.textSecondary};
      margin: 0.25rem 0 0;
      opacity: 0.7;
      line-height: 1.3;
    `;
  }}
`;

// ============================
// CHAT INPUT
// ============================

export const SendButton = styled.button<{
  isDarkMode: boolean;
  disabled?: boolean;
}>`
  ${props => {
    const colors = getThemeColors(props.isDarkMode);
    return css`
      padding: 0.75rem;
      background: ${colors.primary};
      color: white;
      border: none;
      border-radius: 20px;
      cursor: ${props.disabled ? 'not-allowed' : 'pointer'};
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      min-width: 44px;
      opacity: ${props.disabled ? 0.5 : 1};

      &:hover:not(:disabled) {
        background: ${colors.primaryHover};
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(243, 68, 82, 0.3);
      }

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px ${colors.focusRing};
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    `;
  }}
`;

// ============================
// WELCOME TOOLTIP
// ============================

export const TooltipWrapper = styled.div<{ visible: boolean }>`
  position: fixed;
  bottom: 96px;
  right: 24px;
  z-index: 1001;
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.95)'};
  pointer-events: ${props => props.visible ? 'all' : 'none'};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    right: 16px;
    bottom: 84px;
  }
`;

export const TooltipBubble = styled.div`
  background: #ffffff;
  color: #2d2d2d;
  padding: 0.75rem 1rem;
  border-radius: 12px 12px 4px 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  font-size: 0.875rem;
  line-height: 1.4;
  max-width: 220px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  position: relative;

  strong {
    display: block;
    margin-bottom: 0.2rem;
    color: #f34452;
    font-size: 0.9rem;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 18px;
    border: 8px solid transparent;
    border-top-color: #ffffff;
    border-bottom: none;
    filter: drop-shadow(0 2px 2px rgba(0,0,0,0.08));
  }

  &:hover {
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  }
`;

export const TooltipDismiss = styled.button`
  position: absolute;
  top: 6px;
  right: 8px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 3px;

  &:hover {
    color: #333;
    background: #f0f0f0;
  }
`;
